import { useState } from 'react'
import { useLoaderData, useNavigate } from 'react-router-dom'
import axios from 'axios'
import {
  Box,
  Button,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from '@mui/material'
import PageAppBar from 'components/PageAppBar'
import { ResourceModel } from 'types'
import { base_url } from 'env'
import { UploadFile } from '@mui/icons-material'

// --- Composant réutilisable pour l'upload ---
interface FileUploaderProps {
  resourceId: string
  fileTypeKey: 'video' | 'mobilePdf' | 'paperPdf' | 'mobilePng' | 'paperPng'
  label: string
  acceptedFormats: string
  onUploadSuccess: () => void
}

function FileUploader({
  resourceId,
  fileTypeKey,
  label,
  acceptedFormats,
  onUploadSuccess,
}: FileUploaderProps) {
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [isUploading, setIsUploading] = useState(false)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setSelectedFile(e.target.files[0])
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return
    setIsUploading(true)

    const formData = new FormData()
    formData.append(fileTypeKey, selectedFile)
    const token = localStorage.getItem('token')

    try {
      await axios.post(`${base_url}/resource/${resourceId}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${token}`,
        },
      })
      alert(`Fichier "${label}" uploadé avec succès !`)
      onUploadSuccess()
    } catch (error) {
      alert("Erreur lors de l'upload.")
      console.error(error)
    } finally {
      setIsUploading(false)
      setSelectedFile(null)
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
      <Button
        variant="outlined"
        component="label"
        startIcon={<UploadFile />}
        sx={{ flexShrink: 0 }}
      >
        Choisir {label}
        <input
          type="file"
          hidden
          accept={acceptedFormats}
          onChange={handleFileChange}
        />
      </Button>
      {selectedFile && (
        <Typography variant="body2" noWrap>
          {selectedFile.name}
        </Typography>
      )}
      {selectedFile && (
        <Button
          onClick={handleUpload}
          disabled={isUploading}
          variant="contained"
          size="small"
        >
          {isUploading ? <CircularProgress size={20} /> : 'Uploader'}
        </Button>
      )}
    </Box>
  )
}

export default function ResourceUploadPage() {
  const initialResource = useLoaderData() as ResourceModel
  const navigate = useNavigate()

  // On a juste besoin de l'ID, pas besoin de re-fetch les données ici
  const resourceId = initialResource.id!

  // Pas besoin de rafraîchir la page entière, un simple message suffit.
  const handleUploadSuccess = () => {
    // On pourrait potentiellement re-naviguer ou afficher un message de succès plus élaboré
    console.log(
      'Upload réussi, la page parente (preview) reflétera le changement au prochain chargement.'
    )
  }

  return (
    <PageAppBar pageName={`Gérer Fichiers: ${initialResource.title}`}>
      <Box
        sx={{
          p: 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 4,
        }}
      >
        <Button
          variant="outlined"
          onClick={() => navigate(`/resource/${resourceId}`)}
        >
          Retour à l'aperçu
        </Button>

        {initialResource.type === 'local' ? (
          <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Uploader des Fichiers Locaux
              </Typography>
              <FileUploader
                resourceId={resourceId}
                fileTypeKey="video"
                label="Vidéo"
                acceptedFormats="video/mp4"
                onUploadSuccess={handleUploadSuccess}
              />
              <FileUploader
                resourceId={resourceId}
                fileTypeKey="mobilePdf"
                label="PDF Mobile"
                acceptedFormats="application/pdf"
                onUploadSuccess={handleUploadSuccess}
              />
              <FileUploader
                resourceId={resourceId}
                fileTypeKey="paperPdf"
                label="PDF Papier"
                acceptedFormats="application/pdf"
                onUploadSuccess={handleUploadSuccess}
              />
              <FileUploader
                resourceId={resourceId}
                fileTypeKey="mobilePng"
                label="PNG Mobile"
                acceptedFormats="image/png"
                onUploadSuccess={handleUploadSuccess}
              />
              <FileUploader
                resourceId={resourceId}
                fileTypeKey="paperPng"
                label="PNG Papier"
                acceptedFormats="image/png"
                onUploadSuccess={handleUploadSuccess}
              />
            </CardContent>
          </Card>
        ) : (
          <Card sx={{ p: 2, width: '100%', maxWidth: '800px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Configurer les Liens Distants
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                Cette fonctionnalité n'est pas encore implémentée dans le
                backend.
              </Typography>
              <TextField
                fullWidth
                label="URL de la Vidéo"
                disabled
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                label="URL du PDF Mobile"
                disabled
                sx={{ mb: 1.5 }}
              />
              <TextField
                fullWidth
                label="URL du PDF Papier"
                disabled
                sx={{ mb: 1.5 }}
              />
              {/* etc. */}
            </CardContent>
          </Card>
        )}
      </Box>
    </PageAppBar>
  )
}
